import { useEffect, useRef } from 'react';

/**
 * A custom hook to detect clicks outside of one or more specified DOM elements.
 * Uses the capture phase to ensure it catches clicks even if child components
 * stop event propagation (e.g., inside modals or drawers).
 *
 * @param {React.RefObject | React.RefObject[]} refs - A single ref or an array of refs to exclude from the "outside" detection.
 * @param {Function} handler - The callback function to execute when an outside click is detected.
 * @param {boolean} [active=true] - Whether the listener should be active.
 */
export function useOutsideClick(refs, handler, active = true) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const refsRef = useRef(refs);
  refsRef.current = refs;

  useEffect(() => {
    if (!active) return undefined;

    const listener = (event) => {
      const currentRefs = refsRef.current;
      const refArray = Array.isArray(currentRefs) ? currentRefs : [currentRefs];

      const isInside = refArray.some((ref) => {
        return ref && ref.current && ref.current.contains(event.target);
      });

      if (!isInside) {
        handlerRef.current?.(event);
      }
    };

    // Use capture phase (true) so the event is caught before any nested element
    // can call stopPropagation() on it (e.g. inside modals).
    document.addEventListener('mousedown', listener, true);
    document.addEventListener('touchstart', listener, true);

    return () => {
      document.removeEventListener('mousedown', listener, true);
      document.removeEventListener('touchstart', listener, true);
    };
  }, [active]);
}
